import React, { type FC, type PropsWithChildren, useEffect, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'

import { type AuthenticationStatus, NotificationSeverity, Translation } from '../../../domain'

import { LogoutReason } from '../../../application'

import { useAsyncCallback } from '../Callback'
import { type BaseCommonProps, Button } from '../common'
import { boldFormatter, FormattedTranslation, useTranslator } from '../Internationalization'
import { useRouting } from '../routing'
import { useAppUsecases } from '../State'

type Props = Readonly<{ actionName: Translation, onConfirm?: VoidFunction, onClose?: VoidFunction, isOpen: boolean }> & BaseCommonProps

const wrapperStyle: SxProps<Theme> = { px: 4, pt: 3, pb: 1 }
const dialogContentStyle: SxProps<Theme> = { ...wrapperStyle, pb: 0 }
const btnWrapperStyle: SxProps<Theme> = { px: 3, pt: 0 }
const btnStyle: SxProps<Theme> = { fontSize: 'body1.fontSize' }

/**
 * Stable id for the post-dismiss Toast (Step 9) - lets the cleanup below call `dismiss()` on it
 * directly, since `toastUsecase.show()` doesn't return the id it generated.
 */
const TOAST_DEDUPE_KEY = 'permissions-reduced'

const Title: FC = () => <FormattedTranslation id={Translation.REDUCED_PERMISSIONS_TITLE} />

const Warning: FC<PropsWithChildren<Props>> = ({ onConfirm, actionName, children, isOpen, onClose, ...props }) => (
    <Dialog open={isOpen} fullWidth maxWidth="sm" sx={wrapperStyle} data-testid="permissions-issues-modal" {...props}>
        <DialogTitle sx={wrapperStyle} variant="subtitle1" align="left" fontWeight={700}>
            <Title />
        </DialogTitle>
        <DialogContent sx={dialogContentStyle}>{children}</DialogContent>
        <DialogActions sx={btnWrapperStyle}>
            <Button onClick={onClose} sx={btnStyle} variant="primaryGhost" data-testid="dismiss">
                <FormattedTranslation id={Translation.DISMISS} />
            </Button>
            <Button disabled={!onConfirm} onClick={onConfirm} sx={btnStyle} variant="primary" data-testid="primary">
                <FormattedTranslation id={actionName} />
            </Button>
        </DialogActions>
    </Dialog>
)

export const PermissionsReducedModal: FC<{ sessionObsolescenceStatus: AuthenticationStatus }> = ({
    sessionObsolescenceStatus: { isMfaRequired, isMfaEnabled },
}) => {
    const { missingPermissionsUsecase, logoutUsecase, toastUsecase } = useAppUsecases()
    const router = useRouting()
    const translator = useTranslator()

    const [isDismissed, setDismissed] = useState<boolean>(false)

    /** Logout if MFA is enabled, otherwise it reloads the session */
    const [reload, loading] = useAsyncCallback(async () => {
        await missingPermissionsUsecase.reload()
        router.redirectHome()
    }, [missingPermissionsUsecase])

    const isMfa = !isMfaEnabled && isMfaRequired
    const actionName = isMfa ? Translation.LOGOUT : Translation.RELOAD_SESSION
    const confirm: VoidFunction = () => (isMfa ? logoutUsecase.logout(LogoutReason.MANUAL_LOGOUT) : reload())

    /**
     * Once dismissed, the Dialog is replaced by a persistent Toast carrying the same title/action -
     * deliberate: dismissing the dialog shouldn't let the user forget an unresolved MFA/session issue.
     * `persist: true` (Step 9) because this reflects an ongoing state the user hasn't resolved, not a
     * one-off event that should time out on its own.
     */
    useEffect(() => {
        if (!isDismissed) {
            return
        }

        toastUsecase.show({
            severity: NotificationSeverity.WARNING,
            title: translator.formatTranslation(Translation.REDUCED_PERMISSIONS_TITLE),
            action: {
                label: translator.formatTranslation(actionName),
                onClick: () => {
                    toastUsecase.dismiss(TOAST_DEDUPE_KEY)
                    confirm()
                },
            },
            persist: true,
            dedupeKey: TOAST_DEDUPE_KEY,
        })

        return () => toastUsecase.dismiss(TOAST_DEDUPE_KEY)
    }, [isDismissed])

    return (
        <Warning actionName={actionName} onConfirm={!loading ? confirm : undefined} isOpen={!isDismissed} onClose={() => setDismissed(true)}>
            <FormattedTranslation
                id={isMfa ? Translation.MFA_ENFORCED_NOTIFICATION : Translation.PERMISSIONS_CHANGED_NOTIFICATION}
                values={{ b: boldFormatter }}
            />
        </Warning>
    )
}
